"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  motion,
  AnimatePresence,
  type Variants,
  cubicBezier,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { listMenu, type MenuItem } from "@/lib/service/menu.service";

type Category = {
  id: number;
  name: string;
  slug?: string;
  createdAt?: string | number;
  priority?: number;
};

async function fetchCategories(): Promise<Category[]> {
  const res = await fetch("/api/category/getAll", {
    method: "GET",
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`List failed (${res.status})`);
  const data = await res.json();
  return Array.isArray(data?.categories) ? data.categories : data;
}

function getItemCategoryKeys(it: any) {
  const id =
    it?.categoryId ??
    it?.category_id ??
    (typeof it?.category === "number" ? it?.category : it?.category?.id);

  const name =
    it?.categoryName ??
    it?.category_name ??
    it?.category?.name ??
    it?.category?.title;

  const slug =
    it?.categorySlug ??
    it?.category_slug ??
    it?.category?.slug ??
    (typeof name === "string"
      ? name.toLowerCase().replace(/\s+/g, "-")
      : undefined);

  return { id, name, slug };
}

export default function SignatureDishesSection() {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCat, setActiveCat] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setCategoryLoading(true);

        const [menu, cats] = await Promise.all([listMenu(), fetchCategories()]);
        if (!mounted) return;

        setItems(menu || []);

        const sortedCats = [...(cats || [])].sort((a, b) => {
          const aKey = a.createdAt ? +new Date(a.createdAt) : a.id;
          const bKey = b.createdAt ? +new Date(b.createdAt) : b.id;
          return bKey - aKey;
        });

        const latestThree = sortedCats;
        const filteredLatest = sortedCats.filter(
          (cat) => Number(cat.priority) === 1,
        );

        setCategories(filteredLatest);

        if (!activeCat && latestThree.length) {
          setActiveCat(filteredLatest[0]);
        }
      } catch (e) {
        console.error("Failed to load menu/categories:", e);
      } finally {
        if (mounted) {
          setLoading(false);
          setCategoryLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredDishes = useMemo(() => {
    if (!activeCat) return [];

    return items
      .filter((it) => {
        const { id, name, slug } = getItemCategoryKeys(it);

        if (id && activeCat.id && Number(id) === Number(activeCat.id))
          return true;
        if (slug && activeCat.slug && slug === activeCat.slug) return true;
        if (
          name &&
          activeCat.name &&
          name.toLowerCase().trim() === activeCat.name.toLowerCase().trim()
        )
          return true;

        return false;
      })
      .sort((a: any, b: any) => {
        const aTime = a.createdAt ? +new Date(a.createdAt) : a.id;
        const bTime = b.createdAt ? +new Date(b.createdAt) : b.id;
        return bTime - aTime;
      })
      .slice(0, 4);
  }, [items, activeCat]);

  // Determine what data to show
  const data = useMemo(() => {
    if (loading) return [];
    if (filteredDishes.length > 0) return filteredDishes;
    return [];
  }, [loading, filteredDishes]);

  /** ---------------- Animations (typed) ---------------- */
  const easeOutCubic = cubicBezier(0.0, 0.0, 0.58, 1);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: easeOutCubic },
    },
  };

  const dishVariantsLeft: Variants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: easeOutCubic },
    },
  };

  const dishVariantsRight: Variants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: easeOutCubic },
    },
  };

  const tabVariants: Variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4, ease: easeOutCubic },
    },
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="px-4 2xl:px-[222px] pt-5 xl:pt-[111px] bg-[url('/imgs/dishes-bg.png')] bg-cover bg-center bg-no-repeat"
    >
      <motion.div
        variants={containerVariants}
        className="max-w-[774px] w-full mx-auto center text-center flex-col gap-3.5 lg:gap-5 lg:px-10"
      >
        <motion.h1
          variants={itemVariants}
          className="text-[37.78px] sm:text-[68px] leading-[86%] font-bold font-amiri text-spring-wood"
        >
          Our Signature Dishes
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="text-[11.11px] sm:text-xl sm:leading-[22px] text-old-lace"
        >
          Savor the rich flavors of Arabia with every dish, carefully prepared
          to delight your taste buds.
        </motion.p>

        {/* Dynamic Tabs: Latest 3 Categories */}
        <motion.div
          variants={containerVariants}
          className="flex items-center gap-3 sm:gap-5 justify-center flex-wrap"
        >
          {(categoryLoading ? Array.from({ length: 3 }) : categories).map(
            (cat, index) => {
              const isLoading = categoryLoading;
              const c = isLoading ? ({} as Category) : (cat as Category);
              const active = !isLoading && activeCat?.id === c.id;

              return (
                <motion.button
                  key={isLoading ? index : c.id}
                  className={cn(
                    "px-5 py-3.5 border rounded-[6px] text-xs font-semibold transition",
                    active
                      ? "bg-dolly text-[#1B1B1B] shadow-[inset_0px_-4px_4px_0px_rgba(131,104,16,1),inset_0px_4px_4px_0px_rgba(255,255,141,1)]"
                      : "bg-transparent text-white border-white/20 hover:bg:white/10",
                  )}
                  onClick={() => !isLoading && setActiveCat(c)}
                  disabled={isLoading}
                  variants={tabVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {isLoading ? (
                    <span className="inline-block w-16 h-3 bg-white/10 rounded animate-pulse" />
                  ) : (
                    c.name
                  )}
                </motion.button>
              );
            },
          )}
        </motion.div>
      </motion.div>

      <motion.div
        variants={containerVariants}
        className="max-w-[1284px] w-full mx-auto relative mt-10 isolate"
      >
        {/* BG Shape */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="size-full absolute top-[50px] left-[52%] -translate-x-1/2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 954 709"
            width="100%"
            height={709}
            fill="none"
            className="z-[-50] lg:block hidden"
          >
            <path
              d="M19.8872 0.918924C19.8872 0.918924 -47.9399 135.3 78.0356 223.054C204.011 310.809 365.111 157.99 566.216 108.078C767.32 58.166 913.465 108.078 898.498 269.729C883.53 431.38 350.808 345.243 148.252 386.476C-54.3039 427.71 21.0212 640.233 119.023 685.258C217.025 730.283 335.766 702.263 455.824 631.946C575.882 561.629 896.027 550.334 952.377 631.946"
              stroke="white"
              strokeWidth="2.32601"
            />
          </svg>
        </motion.div>

        {/* Empty state */}
        <AnimatePresence>
          {!loading && data.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-16 text-old-lace"
            >
              <p className="text-xl font-amiri">
                No dishes available in this category
              </p>
              <p className="mt-2">Please select another category</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dishes */}
        <div className="flex flex-col gap-0">
          <AnimatePresence mode="wait">
            {(loading ? Array.from({ length: 4 }) : data).map((dish, index) => {
              const isRight = index % 2 !== 0;
              const typedDish = dish as MenuItem;

              return (
                <motion.div
                  key={loading ? index : typedDish.id}
                  className={cn(
                    "flex items-center",
                    isRight ? "self-end" : "self-start",
                    index === 1 && "sm:-translate-y-[100px]",
                    index === 2 && "sm:-translate-y-[200px]",
                    index === 3 &&
                      "2xl:-translate-y-[280px] sm:-translate-y-[330px]",
                  )}
                  variants={isRight ? dishVariantsRight : dishVariantsLeft}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  transition={{ delay: index * 0.2 }}
                >
                  {/* Image */}
                  <motion.div
                    className={cn(
                      "size-[186.64px] sm:size-[310px] relative center shrink-0",
                      isRight ? "order-2" : "order-1",
                    )}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={310}
                      height={310}
                      viewBox="0 0 310 310"
                      fill="none"
                      className={cn(
                        "absolute top-0 -z-10 sm:size-[310px] size-[186.64px]",
                        isRight ? "right-0" : "left-0",
                      )}
                    >
                      <circle
                        cx="154.535"
                        cy="155.453"
                        r="154.356"
                        stroke="#F0DC9C"
                        strokeWidth="0.348901"
                      />
                    </svg>
                    <Link
                      href={`/food/${typedDish?.id}`}
                      className="sm:size-[257px] size-[155.24px]"
                    >
                      {loading ? (
                        <div className="h-full w-full rounded-full bg-white/10 animate-pulse" />
                      ) : (
                        <Image
                          src={typedDish.imageUrl || "/imgs/dish-1.png"}
                          alt={typedDish.name}
                          width={257}
                          height={257}
                          className="size-full relative z-50 object-cover rounded-full"
                        />
                      )}
                    </Link>
                  </motion.div>

                  {/* Card */}
                  <motion.div
                    className={cn(
                      "max-w-[228.17px] sm:max-w-[377.84px] w-full h-[98.51px] sm:h-[163.13px] center flex-col items-start bg-gradient-to-b from-[#151A1F] to-[#0E1317] shadow-[0px_0px_46.52px_0px_rgba(212,175,55,0.20)] z-20 relative",
                      isRight
                        ? "order-1 pr-[60px] pl-3 translate-x-[60px]"
                        : "order-2 pl-[60px] pr-3 -translate-x-[60px]",
                    )}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0px 0px 60px 0px rgba(212,175,55,0.30)",
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.h2
                      className="text-[15.45px] sm:text-[25.59px] leading-[86%] font-bold font-amiri text-spring-wood"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      {loading ? (
                        <span className="inline-block h-5 w-40 bg-white/10 rounded animate-pulse" />
                      ) : (
                        typedDish.name
                      )}
                    </motion.h2>

                    <motion.p
                      className="text-[9.83px] sm:text-[16.28px] leading-[102%] text-old-lace my-[7.02px] sm:my-4 line-clamp-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.4 }}
                    >
                      {loading ? (
                        <span className="inline-block h-4 w-64 bg-white/10 rounded animate-pulse" />
                      ) : (
                        typedDish.description ||
                        "Delicious and freshly prepared."
                      )}
                    </motion.p>

                    {!loading && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.1 + 0.5 }}
                      >
                        <Link
                          href={`/food/${typedDish.id}`}
                          className="flex items-center sm:text-base text-[9.83px] text-old-gold gap-1 sm:gap-2 font-semibold group"
                        >
                          <motion.span
                            whileHover={{ x: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            Order now
                          </motion.span>
                          <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={44}
                            height={13}
                            viewBox="0 0 44 13"
                            fill="none"
                            className="max-sm:w-[26.27px]"
                            whileHover={{ x: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            <line
                              x1="0.0488281"
                              y1="6.00169"
                              x2="31.9179"
                              y2="6.00169"
                              stroke="#D4AF37"
                              strokeWidth="1.163"
                            />
                            <circle
                              cx="37.733"
                              cy="6.58321"
                              r="5.76199"
                              fill="#D4AF37"
                              stroke="#D4AF37"
                              strokeWidth="0.106053"
                            />
                          </motion.svg>
                        </Link>
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.section>
  );
}
